import {
  ACCESS_TYPE,
  ACTOR_TYPE,
  ROLE,
  TEAM_ROLE,
} from '@/shared/data/permissionSystem.js'
import OpeningTimesIcon from '@/assets/images/icons/opening-times.svg'
import AddFileIcon from '@/assets/images/icons/add-file.svg'
import VideoIcon from '@/assets/images/icons/video.svg'
import VideosIcon from '@/assets/images/icons/videos.svg'
import ConstructionIcon from '@/assets/images/icons/construction.svg'
import { computed } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import BasketballIcon from '@/assets/images/icons/basketball.svg'
import AddIcon from '@/assets/images/icons/add.svg'
import RecyclingIcon from '@/assets/images/icons/recycling.svg'
import { router } from '@inertiajs/vue3'
import SettingsGearIcon from '@/assets/images/icons/settings-gear-outline.svg'
import UsersMultipleIcon from '@/assets/images/icons/users-multiple.svg'
import FolderIcon from '@/assets/images/icons/folder.svg'
import BookmarkIcon from '@/assets/images/icons/bookmark.svg'
import ChessKingOutline from '@/assets/images/icons/chess-king-outline.svg'
import GoldCoinIcon from '@/assets/images/icons/gold-coin.svg'
import ShopIcon from '@/assets/images/icons/shop.svg'
import LeaveIcon from '@/assets/images/icons/leave.svg'
import axios from 'axios'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import SparksIcon from '@/assets/images/icons/sparks.svg'
import ShoppingLabelOutlineIcon from '@/assets/images/icons/shopping-label-outline.svg'

const _main = [
  {
    name: 'My Sessions',
    icon: OpeningTimesIcon,
    href: {
      [ACTOR_TYPE.MENTEE]: '/sessions',
      [ACTOR_TYPE.MENTOR]: '/mentor/sessions',
      [ACTOR_TYPE.NONE]: null,
    },
  },
  {
    name: 'Session Recordings',
    icon: VideoIcon,
    href: {
      [ACTOR_TYPE.MENTEE]: '/recordings',
      [ACTOR_TYPE.MENTOR]: '/recordings',
      [ACTOR_TYPE.NONE]: null,
    },
  },
  {
    name: 'Help Requests',
    icon: AddFileIcon,
    href: {
      [ACTOR_TYPE.MENTEE]: '/opportunities',
      [ACTOR_TYPE.MENTOR]: '/opportunities',
      [ACTOR_TYPE.NONE]: '/opportunities',
    },
  },
]

export const useNavigationElements = () => {
  const { accessControl, isUserLoggedIn } = usePermissionSystem()
  const { currentUser } = useCurrentUser()

  const mainNavigation = computed(() => {
    return _main.filter((element) => {
      if (element.dropdown) {
        element.dropdown = element.dropdown.filter(
          (element) => element.href[accessControl.value.role],
        )
        return element.dropdown.length > 0
      } else {
        return element.href[accessControl.value.actorType]
      }
    })
  })
  const mainNavigationItemsInMoreButton = computed(() => {
    return _main.filter((item) => item.isInMoreButton)
  })

  const mentorDropdownNavigation = computed(() => {
    const teamNavbarElement =
      accessControl.value.teamRole === TEAM_ROLE.OWNER
        ? {
            as: 'InertiaLink',
            name: 'View Team',
            icon: BasketballIcon,
            href: '/team/members',
          }
        : accessControl.value.teamRole === TEAM_ROLE.NONE
          ? {
              as: 'InertiaLink',
              name: 'Create Team',
              icon: AddIcon,
              href: '/team/new',
            }
          : null

    return [
      {
        as: 'button',
        name:
          accessControl.value.actorType === ACTOR_TYPE.MENTOR
            ? 'Switch to mentee profile'
            : 'Switch to mentor profile',
        icon: RecyclingIcon,
        hasBottomSeparator: true,
        actions: {
          onClick: () => {
            router.patch('/switch-actor')
          },
        },
      },
      teamNavbarElement,
      {
        as: 'InertiaLink',
        name: 'Saved Mentors',
        icon: BookmarkIcon,
        href: '/lists',
      },
      {
        as: 'InertiaLink',
        name: 'My Collections',
        icon: FolderIcon,
        href: '/collections?query_type=my_collections',
      },
      {
        as: 'InertiaLink',
        name: 'Video Library',
        icon: VideosIcon,
        href: '/videos',
      },
      {
        as: 'InertiaLink',
        name: 'City Squads',
        icon: ConstructionIcon,
        href: '/cities',
      },
      {
        as: 'InertiaLink',
        name: 'Connections',
        icon: UsersMultipleIcon,
        href: '/connections',
        iconClasses: 'tw-stroke-neutral-700',
        hasBottomSeparator: true,
      },
      {
        as: 'InertiaLink',
        name: 'AI Matching',
        icon: SparksIcon,
        href: '/matches',
      },
      {
        as: 'InertiaLink',
        name: 'Leaderboard',
        icon: ChessKingOutline,
        href: '/leaderboard',
      },
      {
        as: 'InertiaLink',
        name: 'Partner Deals',
        icon: ShoppingLabelOutlineIcon,
        href: '/partner-deals',
      },
      {
        as: 'InertiaLink',
        name: 'Share & Earn',
        icon: GoldCoinIcon,
        href: '/referrals',
      },
      {
        as: 'InertiaLink',
        name: 'Settings',
        icon: SettingsGearIcon,
        href: '/settings/account/sessions',
        hasBottomSeparator: true,
      },
      {
        as: 'InertiaLink',
        name: 'GrowthExpert',
        icon: ShopIcon,
        href: '/growthexpert',
        hasBottomSeparator: true,
      },
      {
        as: 'button',
        name: 'Log out',
        icon: LeaveIcon,
        href: '/users/sign_out',
        attrs: {
          test_id: 'logout-button',
        },
        actions: {
          onClick: async () => {
            await axios.delete('/users/sign_out')
            window.location.href = '/users/sign_in'
            window.segmentAnalytics.reset()
          },
        },
      },
    ].filter(Boolean)
  })
  const menteeDropdownNavigation = computed(() => {
    const teamNavbarElement =
      accessControl.value.teamRole === TEAM_ROLE.OWNER
        ? {
            as: 'InertiaLink',
            name: 'View Team',
            icon: BasketballIcon,
            href: '/team/members',
          }
        : accessControl.value.teamRole === TEAM_ROLE.NONE
          ? {
              as: 'InertiaLink',
              name: 'Create Team',
              icon: AddIcon,
              href: '/team/new',
            }
          : null

    return [
      teamNavbarElement,
      {
        as: 'InertiaLink',
        name: 'Saved Mentors',
        icon: BookmarkIcon,
        href: '/lists',
      },
      {
        as: 'InertiaLink',
        name: 'My Collections',
        icon: FolderIcon,
        href: '/collections?query_type=my_collections',
      },
      {
        as: 'InertiaLink',
        name: 'Video Library',
        icon: VideosIcon,
        href: '/videos',
      },
      {
        as: 'InertiaLink',
        name: 'City Squads',
        icon: ConstructionIcon,
        href: '/cities',
      },
      {
        as: 'InertiaLink',
        name: 'Connections',
        icon: UsersMultipleIcon,
        href: '/connections',
        iconClasses: 'tw-stroke-neutral-700',
        hasBottomSeparator: true,
      },
      {
        as: 'InertiaLink',
        name: 'AI Matching',
        icon: SparksIcon,
        href: '/matches',
      },
      {
        as: 'InertiaLink',
        name: 'Leaderboard',
        icon: ChessKingOutline,
        href: '/leaderboard',
      },
      accessControl.value.accessType === ACCESS_TYPE.GROWTHMENTOR
        ? {
            as: 'InertiaLink',
            name: 'Partner Deals',
            icon: ShoppingLabelOutlineIcon,
            href: '/partner-deals',
          }
        : null,
      {
        as: 'InertiaLink',
        name: 'Share & Earn',
        icon: GoldCoinIcon,
        href: '/referrals',
      },
      {
        as: 'InertiaLink',
        name: 'Settings',
        icon: SettingsGearIcon,
        href: '/settings/account/account',
        hasBottomSeparator: true,
      },
      {
        as: 'button',
        name: 'Log out',
        icon: LeaveIcon,
        actions: {
          onClick: async () => {
            await axios.delete('/users/sign_out')
            window.location.href = '/users/sign_in'
            window.segmentAnalytics.reset()
          },
        },
      },
    ].filter(Boolean)
  })
  const dropdownNavigation = computed(() => {
    if (!isUserLoggedIn.value) {
      return { [ROLE.MENTOR]: [], [ROLE.MENTEE]: [], [ROLE.GUEST]: [] }
    }
    return {
      [ROLE.MENTEE]: menteeDropdownNavigation.value,
      [ROLE.MENTOR]: mentorDropdownNavigation.value,
      [ROLE.GUEST]: [],
    }
  })

  return {
    mainNavigation,
    mainNavigationItemsInMoreButton,
    dropdownNavigation,
  }
}
